/**
 * Environment Connection Helper
 * Pre-flight checks and helpers for Salesforce org connection flow
 */

import { checkCanConnectOrg, associateOrgWithEnvironment } from '../services/environmentService';
import { LIMIT_MESSAGES } from '../config/environmentLimits';

export interface ConnectionCheckResult {
  allowed: boolean;
  message?: string;
  messageType?: 'error' | 'warning' | 'info';
  environment_id?: string;
  is_new_environment: boolean;
}

/**
 * Check if user can connect a new Salesforce org
 * Call this BEFORE starting OAuth flow
 */
export async function checkConnectionLimits(
  accountId: string,
  subscriptionPlan: string | null,
  isSandbox: boolean,
  productionOrgId: string
): Promise<ConnectionCheckResult> {
  try {
    const result = await checkCanConnectOrg(
      accountId,
      subscriptionPlan,
      isSandbox,
      productionOrgId
    );

    if (!result.canConnect) {
      return {
        allowed: false,
        message: result.reason || 'Cannot connect this org',
        messageType: 'error',
        is_new_environment: result.is_new_environment
      };
    }

    // Can connect - provide helpful message
    if (result.is_new_environment) {
      return {
        allowed: true,
        message: 'This will create a new environment',
        messageType: 'info',
        is_new_environment: true
      };
    } else {
      const orgCount = result.org_count_in_environment || 0;
      return {
        allowed: true,
        message: `This ${isSandbox ? 'sandbox' : 'org'} will join your existing environment (${orgCount + 1}/5 orgs)`,
        messageType: 'info',
        environment_id: result.environment_id,
        is_new_environment: false
      };
    }
  } catch (error) {
    console.error('Error checking connection limits:', error);
    return {
      allowed: false,
      message: 'Error checking org limits. Please try again.',
      messageType: 'error',
      is_new_environment: false
    };
  }
}

/**
 * Get production org ID from Salesforce metadata
 * This should be called during or after OAuth callback
 */
export async function getProductionOrgIdFromSalesforce(
  accessToken: string,
  instanceUrl: string
): Promise<{ productionOrgId: string; isSandbox: boolean }> {
  try {
    // Query organization info
    const response = await fetch(
      `${instanceUrl}/services/data/v59.0/query?q=${encodeURIComponent(
        'SELECT Id, IsSandbox, InstanceName FROM Organization LIMIT 1'
      )}`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to query organization info');
    }

    const data = await response.json();
    const org = data.records[0];

    if (!org) {
      throw new Error('No organization record found');
    }

    const isSandbox = org.IsSandbox;

    // For production orgs, production_org_id is their own ID
    // For sandboxes, we need to get the SourceOrganizationId
    if (!isSandbox) {
      return {
        productionOrgId: org.Id,
        isSandbox: false
      };
    }

    // For sandboxes, query the sandbox info to get parent production org ID
    console.log('Querying SandboxInfo for parent org ID...');
    const sandboxResponse = await fetch(
      `${instanceUrl}/services/data/v59.0/tooling/query?q=${encodeURIComponent(
        'SELECT SourceId FROM SandboxInfo LIMIT 1'
      )}`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    console.log('SandboxInfo response status:', sandboxResponse.status);

    if (!sandboxResponse.ok) {
      const errorText = await sandboxResponse.text();
      console.error('SandboxInfo API failed:', {
        status: sandboxResponse.status,
        statusText: sandboxResponse.statusText,
        error: errorText
      });
      throw new Error(`SandboxInfo API failed with status ${sandboxResponse.status}: ${errorText}`);
    }

    const sandboxData = await sandboxResponse.json();
    console.log('SandboxInfo response:', JSON.stringify(sandboxData, null, 2));

    const sandboxInfo = sandboxData.records[0];

    if (!sandboxInfo) {
      console.error('No SandboxInfo records found');
      throw new Error('No SandboxInfo records found - cannot determine parent production org');
    }

    if (!sandboxInfo.SourceId) {
      console.error('SandboxInfo found but SourceId is null:', sandboxInfo);
      throw new Error('SandboxInfo.SourceId is null - cannot determine parent production org');
    }

    console.log('Successfully retrieved parent production org ID:', sandboxInfo.SourceId);
    return {
      productionOrgId: sandboxInfo.SourceId,
      isSandbox: true
    };
  } catch (error) {
    console.error('Error getting production org ID:', error);
    throw error;
  }
}

/**
 * Associate newly connected org with environment
 * Call this AFTER org is created in sf_orgs table
 */
export async function linkOrgToEnvironment(
  accountId: string,
  orgId: string,
  productionOrgId: string,
  isSandbox: boolean,
  orgName: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await associateOrgWithEnvironment(
      accountId,
      orgId,
      productionOrgId,
      isSandbox,
      orgName
    );

    if (!result.success) {
      return {
        success: false,
        error: result.error || 'Failed to link org to environment'
      };
    }

    return { success: true };
  } catch (error) {
    console.error('Error linking org to environment:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Get user-friendly message for connection status
 */
export function getConnectionStatusMessage(
  allowed: boolean,
  isNewEnvironment: boolean,
  isSandbox: boolean,
  orgCount?: number
): string {
  if (!allowed) {
    return LIMIT_MESSAGES.ENVIRONMENT_LIMIT_REACHED;
  }

  if (isNewEnvironment) {
    return `Connecting this ${isSandbox ? 'sandbox' : 'production org'} will create a new environment.`;
  }

  return `This ${isSandbox ? 'sandbox' : 'org'} will join your existing environment (${orgCount || 1}/5 orgs).`;
}
